/**
 * 주문 스냅샷에 저장된 사용자 입력 옵션 답변(customer_input_pairs)을 안전하게 추출한다.
 *
 * 백엔드 계약: 단건 주문은 Order.order_details, 장바구니 주문은 OrderItem.item_details 에
 * `customer_input_pairs = [{ question, answer }, ...]` 형태로 스냅샷된다.
 * 키 부재·비-list·레거시/손상 데이터는 빈 배열로 정규화한다(읽기 전용).
 *
 * @param {object} details - order_details 또는 item_details 객체
 * @returns {Array<{question: string, answer: string|null}>}
 */
export const getCustomerInputPairs = (details) => {
    const pairs = details?.customer_input_pairs;
    if (!Array.isArray(pairs)) return [];
    return pairs.filter(
        (pair) =>
            pair &&
            typeof pair === "object" &&
            typeof pair.question === "string"
    );
};

/**
 * 주문 객체 하나에서 사용자 입력 옵션 답변을 수집한다.
 * 목록/상세 응답의 스냅샷 위치 차이를 흡수한다:
 *  - 단건 주문: order.order_details 또는 order 최상위
 *  - 장바구니 주문: 각 order.items[].item_details 또는 item 최상위
 * 우선순위대로 첫 번째로 값이 있는 위치만 사용해 중복 렌더를 막는다.
 *
 * @param {object} order - 주문(목록 항목 또는 상세) 객체
 * @returns {Array<{question: string, answer: string|null}>}
 */
export const getOrderCustomerInputPairs = (order) => {
    if (!order || typeof order !== "object") return [];

    // 단건 주문: order_details 우선, 없으면 order 최상위.
    const orderLevel = getCustomerInputPairs(order.order_details);
    if (orderLevel.length > 0) return orderLevel;
    const orderTop = getCustomerInputPairs(order);
    if (orderTop.length > 0) return orderTop;

    // 장바구니 주문: 각 항목의 item_details 우선, 없으면 item 최상위.
    return (order.items || []).flatMap((item) => {
        const itemPairs = getCustomerInputPairs(item?.item_details);
        return itemPairs.length > 0 ? itemPairs : getCustomerInputPairs(item);
    });
};
